import { useContext, useEffect, FC, useState } from "react";
import { Modal, Form, InputNumber, notification, Flex } from "antd";
import { useTranslation } from "react-i18next";
import { PackOrderContext } from "../context";
import usePackOrderDetail from "../hooks";
import { BoxShipStatus, BoxStatus, PackBox, PackOrderStatus } from "@/types/packing";
import STableLayout from "@/styles/STableLayout";
import { TableProps } from "antd";
import { AnyObject } from "antd/es/_util/type";
import SText from "@/styles/SText";
import { numFormmater } from "@/pages/Requirement/util";

interface SealOrderProps {
  open: boolean;
  onClose: () => void;
};

type ItemDataType = PackBox & {
  editGross: number;
  editNet: number;
};

const SealOrder: FC<SealOrderProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const { orderData, setOrderData, setBoxShipStatus } = useContext(PackOrderContext);
  const { editBox, doOrder, loading } = usePackOrderDetail(orderData);
  const [states, setStates] = useState<ItemDataType[]>([]);

  useEffect(() => {
    if (open && orderData?.boxes) {
      const items: ItemDataType[] = [];
      orderData.boxes.forEach(box => {
        let netWeight: number = 0;
        box.items.forEach(item => netWeight += (item.netWeight || 0));
        if (box.status !== BoxStatus.SHUTED) items.push({
          ...box,
          editGross: box.grossWeightG ?? 0,
          editNet: netWeight,
        })
      });
      setStates(items);
    }
  }, [open, orderData]);

  const handleWeightChange = (boxId: string, field: 'editGross' | 'editNet', value: number) => {
    setStates(prev => prev.map(item =>
      item.id === boxId ? { ...item, [field]: value } : item
    ));
  };

  const handleConfirm = async () => {
    // 检查是否有未填写净重或毛重的箱子
    const uncompleted = states.filter(item => item.editGross === 0 || item.editNet === 0);
    if (uncompleted.length > 0) {
      notification.error({
        message: t('warehouse.confirmFail'),
        description: t('warehouse.weightNeed'),
      });
      return;
    }

    // 一个一个更新 box 数据
    try {
      for (const item of states) {
        await editBox(
          {
            lengthMm: item.lengthMm,
            widthMm: item.widthMm,
            heightMm: item.heightMm,
            grossWeightG: item.editGross,
            netWeightG: item.editNet,
            packageType: item.packageType,
            marksNumbers: item.marksNumbers,
            remark: item.remark,
          },
          item.id,
          true
        );
      }
      // 更新成功后执行 confirm
      await doOrder('confirm', undefined, true);
      if (orderData.boxes) {
        let status: BoxShipStatus[] = orderData.boxes.map(b => ({
          boxNumber: b.boxNumber,
          packingBoxId: b.id,
          shipmentOrderId: null,
          shipped: false
        }));
        setBoxShipStatus(status)
      }

      // 手动更新 context 中的 orderData
      setOrderData({
        ...orderData,
        status: PackOrderStatus.CONFIRMED,
        boxes: states.map(({ editGross, editNet, ...box }) => ({
          ...box,
          grossWeightG: editGross,
          netWeightG: editNet,
        })),
      });

      onClose();
    } catch (error) {
      notification.error({ message: t('warehouse.sealFail') });
    }
  };

  const columns: TableProps<ItemDataType>['columns'] = [
    {
      title: <SText text={t('warehouse.boxNo')} />,
      width: 120,
      key: "boxNumber",
      fixed: "left",
      render: (_, record) => <SText text={record.boxNumber} />
    },
    {
      title: <SText text={t('warehouse.lengthMm')} />,
      width: 120,
      key: "lengthMm",
      render: (_, record) => <SText text={numFormmater(record.lengthMm)} />
    },
    {
      title: <SText text={t('warehouse.widthMm')} />,
      width: 120,
      key: "widthMm",
      render: (_, record) => <SText text={numFormmater(record.widthMm)} />
    },
    {
      title: <SText text={t('warehouse.heightMm')} />,
      width: 120,
      key: "heightMm",
      render: (_, record) => <SText text={numFormmater(record.heightMm)} />
    },
    {
      title: <SText text={t('warehouse.grossWeightG')} />,
      width: 150,
      key: "editGross",
      render: (_, record) => (
        <Form.Item style={{ margin: 0 }}>
          <InputNumber
            disabled={loading === "PackOrderSeal"}
            min={0}
            value={record.editGross}
            onChange={val => handleWeightChange(record.id, 'editGross', val || 0)}
            placeholder={t('warehouse.grossWeightG')}
            style={{ width: "100%" }}
            changeOnWheel
            formatter={val => numFormmater(Number(val || 0))}
          />
        </Form.Item>
      )
    },
    {
      title: <SText text={t('warehouse.netWeightG')} />,
      width: 150,
      key: "editNet",
      hidden: true,
      render: (_, record) => (
        <Form.Item style={{ margin: 0 }}>
          <InputNumber
            disabled={loading === "PackOrderSeal"}
            min={0}
            value={record.editNet}
            onChange={val => handleWeightChange(record.id, 'editNet', val || 0)}
            placeholder={t('warehouse.netWeightG')}
            style={{ width: "100%" }}
            changeOnWheel
            formatter={val => numFormmater(Number(val || 0))}
          />
        </Form.Item>
      )
    },
  ];

  return (
    <Modal
      title={t('scan.confirm') + ': ' + orderData.number}
      open={open}
      onCancel={onClose}
      onOk={handleConfirm}
      okButtonProps={{ loading: loading === "PackOrderConfirm" || loading === "BoxEdit" }}
      width={1200}
      maskClosable={false}
      centered
    >
      <Flex vertical style={{ height: "60vh" }}>
        <STableLayout
          columns={columns as Array<AnyObject>}
          dataSource={states}
          total={states.length}
          extHeight={200}
          pagination={false}
          scrollX={1000}
          headerChildren={null}
          virtual
        />
      </Flex>
    </Modal>
  )
};

export default SealOrder;
